/**
 * @file    columnsBuilder.tsx
 * @scope   VIEW
 * @summary Builds react-data-table column definitions for the legacy table.
 * @consumes DefaultCell, SingleHorizontalBarChart, DynamicTableTooltipOld
 * @produces buildLegacyColumns
 * @sideEffects QUIRK: mutates `col.displayName` of an unnamed first column.
 */
import React from 'react';
import { TableColumn } from 'react-data-table-component';
import SingleHorizontalBarChart from 'components/Charts/SingleHorizontalBarChart/SingleHorizontalBarChart';
import { TableType } from 'models/WidgetModel';
import Tooltip from '../DynamicTableTooltipOld';
import { getValueFromRow } from '../common/utils/cellIndicators';
import DefaultCell from './cells/DefaultCell';

export interface LegacyColumnsContext {
  payload: any;
  /** Fetched response if any, otherwise the initial one. */
  activeResponse: any;
  selectedValueL1: any;
  decimalPlaces: any;
  displayTimePeriodDelta: boolean;
}

const SIGNIFICANCE_EXTRA_CHARS = 5;

const getMaxContentLength = (col: any, rows: any[]) => {
  const longest = rows
    .filter((row: any) => row[col.field])
    .map((row: any) => row[col.field])
    .reduce((maxLength: number, cell: any) => {
      let length = cell?.value?.toString().length ?? cell.toString().length;
      if (cell?.significanceIndicator || false) length += SIGNIFICANCE_EXTRA_CHARS;
      return Math.max(maxLength, length);
    }, 0);
  return Math.max(longest, col.displayName.length);
};

const toMinWidth = (maxLength: number) => `${Math.max(5, maxLength / 2 + 1)}em`;

const buildDefaultColumn = (col: any, ctx: LegacyColumnsContext) => ({
  name: (
    <Tooltip text={col.displayName}>
      <span>{col.displayName}</span>
    </Tooltip>
  ),
  omit: col.colType === 'Hidden',
  wrap: true,
  tooltip: col.displayName,
  selector: (row: { [x: string]: any }) => ({ value: row[`${col.field}`], field: col.field }),
  sortField: col.field,
  format: (row: { [x: string]: any }) =>
    col.isPercentage
      ? `${getValueFromRow(row, `${col.field}`)}%`
      : row[`${col.field.slice(0, 100)}`],
  cell: (row: { [x: string]: any }) => (
    <DefaultCell
      row={row}
      col={col}
      decimalPlaces={ctx.decimalPlaces}
      displayTimePeriodDelta={ctx.displayTimePeriodDelta}
    />
  ),
});

/** Strategy: which sortable/rowClick flags a column gets. */
const resolveColumnBehaviour = (col: any, sortingType: string, ctx: LegacyColumnsContext) => {
  // QUIRK: `undefined !== null`, so only an explicit null QueriesMetadata reaches the next rules.
  if (ctx.payload?.QueriesMetadata !== null) {
    return { sortable: sortingType === TableType.ENTERPRISE, ignoreRowClick: true };
  }
  if (!col.isLink || !ctx.activeResponse?.data?.idSurveyPanel) {
    return { sortable: true, ignoreRowClick: true };
  }
  return { sortable: col.displayName !== '' };
};

export const buildLegacyColumns = (
  data: any,
  sortingType: string,
  ctx: LegacyColumnsContext
): TableColumn<any>[] =>
  data?.columns.map((col: any, index: number) => {
    const maxLength = getMaxContentLength(col, data?.data ?? []);

    if (index === 0 && col.displayName === '') {
      col.displayName = ctx.selectedValueL1?.label
        ? ctx.selectedValueL1?.label
        : ctx.activeResponse.data.controlData.defaultValueL1.label;
    }

    if (col.displayName === '') {
      return {
        cell: (row: { [x: string]: any }) => (
          <SingleHorizontalBarChart
            value={row[`${col.field}`]}
            isPercentage
            color="#0071c1"
            showLabel={true}
          />
        ),
        ignoreRowClick: true,
      };
    }

    return {
      ...buildDefaultColumn(col, ctx),
      ...resolveColumnBehaviour(col, sortingType, ctx),
      minWidth: toMinWidth(maxLength),
    };
  });
